/**
 * lib/auth-chofer.ts
 *
 * Single source of truth para identificar dinámicamente si un usuario
 * tiene acceso al portal del chofer (/chofer/*), sin hardcodear el
 * string "chofer" del nombre del perfil.
 *
 * Estrategia: chequear permisos GRANULARES con prefijo `chofer.*` o
 * `firma.*` (los que están en el seed `seed-perfiles.ts` y catálogo
 * `lib/permisos-catalogo.ts` bajo el grupo "Chofer").
 *
 * Esto permite:
 *   - El admin crear perfiles personalizados (ej. "Chofer Senior",
 *     "Supervisor de Campo") que tengan permisos `chofer.*` y aplican
 *     automáticamente sin tocar código
 *   - Cambiar el nombre del perfil "chofer" sin romper auth
 *   - Granularidad: si en el futuro queremos diferenciar "puede ver
 *     viajes" vs "puede firmar", basta chequear permisos específicos
 *
 * Compatible con sistema legacy: además acepta `User.role === "chofer"`
 * como fallback (mientras existan usuarios migrados desde la versión
 * antigua que no tienen perfil moderno asignado).
 */

import type { PermisosEfectivos } from "./permisos-server";

/** Prefijos de permisos que indican acceso a funciones del portal chofer. */
export const PERMISO_PREFIX_CHOFER = "chofer.";
export const PERMISO_PREFIX_FIRMA = "firma.";

/**
 * Permiso mínimo requerido para acceder al portal /chofer.
 * Si en el futuro queremos cambiar el "permiso de entrada", se cambia acá.
 */
export const PERMISO_PORTAL_CHOFER = "chofer.ver_viaje";

/**
 * Devuelve true si el usuario tiene CUALQUIER permiso que lo califique
 * como chofer (al menos un permiso del grupo chofer/firma).
 *
 * NO incluye admin — admin tiene acceso a todo pero esa decisión la
 * toma el caller (typically `requireChofer` lo permite vía `esAdmin`).
 *
 * Uso típico:
 *   const ef = await resolverPermisosEfectivos(userId);
 *   const esChofer = esChoferPorPermisos(ef);
 */
export function esChoferPorPermisos(ef: PermisosEfectivos): boolean {
  for (const permiso of ef.permisos) {
    if (
      permiso.startsWith(PERMISO_PREFIX_CHOFER) ||
      permiso.startsWith(PERMISO_PREFIX_FIRMA)
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Versión más estricta: solo true si tiene el permiso mínimo de
 * acceso al portal (`chofer.ver_viaje`). Útil cuando queremos exigir
 * que el usuario pueda ver al menos un viaje (no solo firmar).
 */
export function tienePermisoPortalChofer(ef: PermisosEfectivos): boolean {
  return ef.permisos.has(PERMISO_PORTAL_CHOFER);
}

/**
 * Variant client-friendly: trabaja con un objeto que tiene `permisos`
 * como string[] (el formato que sale del hook usePermisos del browser).
 */
export function esChoferPorPermisosArray(permisos: readonly string[]): boolean {
  for (const permiso of permisos) {
    if (
      permiso.startsWith(PERMISO_PREFIX_CHOFER) ||
      permiso.startsWith(PERMISO_PREFIX_FIRMA)
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Para el endpoint admin que lista "usuarios elegibles para vincular
 * a un Conductor". Filtra users que:
 *   1. Tienen al menos UN permiso `chofer.*` o `firma.*` activo, O
 *   2. Tienen rol legacy "chofer" (fallback para usuarios sin migrar)
 *
 * NO filtra admins porque también podrían querer vincularse como
 * conductor (caso real: dueño de la flota que ocasionalmente maneja).
 */
export interface UserCandidatoChofer {
  id: string;
  email: string;
  name: string | null;
  role: string;
  /** Set de permisos efectivos (puede venir de UsuarioPerfil→Perfil→PerfilPermiso) */
  permisosEfectivos: string[];
}

export function usuarioPuedeSerChofer(user: UserCandidatoChofer): boolean {
  // 1. Permisos modernos
  if (esChoferPorPermisosArray(user.permisosEfectivos)) return true;
  // 2. Legacy
  if (user.role === "chofer") return true;
  return false;
}
